import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type CashAccount } from '../../api.ts';
import { FormModal, FormSection } from '../../components/FormModal.tsx';
import { Badge, ErrorBox, Field, Input, NumCell, Select, Table } from '../../components/ui.tsx';
import { useFormat } from '../purchasing/common.tsx';

type Layout = 'ofx' | 'csv:mercury';

interface Preview {
  layout: Layout;
  currency: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  sourceAccounts: string[];
  warnings: string[];
  lines: {
    key: string;
    date: string;
    amount: string;
    description: string;
    kind: string;
    repeated: boolean;
    /** Mesma data e valor de uma linha já lançada (digitada ou de outro arquivo). */
    possibleDuplicate: boolean;
  }[];
  balances: { date: string; amount: string; origin: string; existing: boolean }[];
  linesNew: number;
  linesRepeated: number;
  linesPossibleDuplicate: number;
}

async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

/**
 * Importar extrato (ADR 0084, Entrega 2b): OFX dos bancos ou CSV do Mercury. Primeiro a prévia,
 * sem gravar nada; depois a importação, tudo ou nada. Linhas que já estão na conta não entram de
 * novo, e o saldo de um dia que já tem saldo informado não é trocado.
 */
export function ImportStatementForm({
  account,
  onDone,
}: {
  account: CashAccount;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [layout, setLayout] = useState<Layout>('ofx');
  const [sourceAccount, setSourceAccount] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  // Possíveis repetições que a pessoa confirmou serem outras linhas: entram na importação.
  const [include, setInclude] = useState<Set<string>>(new Set());
  const body = async () => ({
    accountKey: account.accountKey,
    layout,
    fileName: file!.name,
    contentBase64: await toBase64(file!),
    sourceAccount: sourceAccount || null,
  });
  const check = useMutation({
    mutationFn: async () => api.post<Preview>('/finance/treasury/statements/preview', await body()),
    onSuccess: (p) => {
      setPreview(p);
      setInclude(new Set());
    },
  });
  const save = useMutation({
    mutationFn: async () =>
      api.post('/finance/treasury/statements/import', { ...(await body()), include: [...include] }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['finance', 'treasury'] });
      onDone();
    },
  });
  const ready =
    !!preview && !preview.warnings.includes('choose_source_account') && !!preview.periodStart;
  const money = (v: string) => fmt.money(v, account.currency);
  return (
    <FormModal
      open
      size="xl"
      title={t('finance.import.title')}
      description={t('finance.import.hint', {
        account: `${account.legalEntityCode ?? '—'} · ${account.nickname} · ${account.currency}`,
      })}
      dirty={!!file}
      saving={check.isPending || save.isPending}
      error={save.error ?? check.error}
      saveLabel={ready ? t('finance.import.import') : t('finance.import.preview')}
      onSave={() => (ready ? save.mutate() : file && check.mutate())}
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.import.file')} required>
          <Input
            id="import-file"
            type="file"
            accept=".ofx,.csv"
            required
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              setPreview(null);
              setSourceAccount('');
              if (f) setLayout(f.name.toLowerCase().endsWith('.csv') ? 'csv:mercury' : 'ofx');
            }}
          />
        </Field>
        <Field label={t('finance.import.layout')} required>
          <Select
            id="import-layout"
            value={layout}
            onChange={(e) => {
              setLayout(e.target.value as Layout);
              setPreview(null);
            }}
          >
            <option value="ofx">{t('finance.import.layouts.ofx')}</option>
            <option value="csv:mercury">{t('finance.import.layouts.mercury')}</option>
          </Select>
        </Field>
        {preview && preview.sourceAccounts.length > 1 && (
          <Field label={t('finance.import.sourceAccount')} required>
            <Select
              id="import-source"
              value={sourceAccount}
              onChange={(e) => {
                setSourceAccount(e.target.value);
                setPreview(null);
              }}
            >
              <option value="">{t('finance.import.chooseSource')}</option>
              {preview.sourceAccounts.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </Field>
        )}
      </FormSection>
      {preview && (
        <div className="mt-4 flex flex-col gap-3">
          <p className="text-sm">
            {preview.periodStart
              ? t('finance.import.summary', {
                  start: fmt.date(preview.periodStart),
                  end: fmt.date(preview.periodEnd),
                  fresh: preview.linesNew,
                  repeated: preview.linesRepeated,
                  balances: preview.balances.filter((b) => !b.existing).length,
                })
              : t('finance.import.noPeriod')}
          </p>
          {preview.linesPossibleDuplicate > 0 && (
            <p className="text-sm text-muted">
              {t('finance.import.possibleDuplicateHint', { n: preview.linesPossibleDuplicate })}
            </p>
          )}
          {preview.warnings.map((w) => (
            <p key={w} className="text-sm text-muted">
              {t(`finance.import.warnings.${w}`)}
            </p>
          ))}
          <ErrorBox error={check.error} />
          {preview.lines.length > 0 && (
            <Table
              head={[
                t('finance.cash.fields.date'),
                t('finance.cash.fields.description'),
                { label: t('finance.cash.fields.amount'), align: 'right' },
                '',
              ]}
            >
              {preview.lines.slice(0, 200).map((l, i) => (
                <tr key={i}>
                  <td className="whitespace-nowrap">{fmt.date(l.date)}</td>
                  <td className="text-sm">{l.description}</td>
                  <NumCell>{money(l.amount)}</NumCell>
                  <td>
                    {l.repeated && <Badge tone="neutral">{t('finance.import.repeated')}</Badge>}
                    {l.possibleDuplicate && (
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={include.has(l.key)}
                          onChange={(e) =>
                            setInclude((cur) => {
                              const next = new Set(cur);
                              if (e.target.checked) next.add(l.key);
                              else next.delete(l.key);
                              return next;
                            })
                          }
                        />
                        <Badge tone="off">{t('finance.import.possibleDuplicate')}</Badge>
                        {t('finance.import.includeAnyway')}
                      </label>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      )}
    </FormModal>
  );
}
